/* eslint-disable */

/**
 * Variant-Picker v2 — High-Variant-tauglich (bis 2048 Varianten).
 *
 * v1 hielt ALLE Varianten als JSON in der Seite und suchte die Auswahl clientseitig.
 * Shopify kappt `product.variants` in Liquid aber bei 250 -> jede Variante darueber hinaus
 * landete auf "Nicht verfuegbar" und der Kauf-Button blieb deaktiviert.
 *
 * v2 loest die Auswahl SERVERSEITIG auf: bei jeder Aenderung wird die Section mit
 * `?section_id=<id>&option_values=<ids>` neu geholt und nur die mit
 * `data-dynamic-variant-content` markierten Teilstuecke werden getauscht. Der Picker selbst
 * bleibt stehen und bekommt nur die Zustaende seiner Optionen aktualisiert — dadurch gehen
 * weder Fokus noch die Klick-Auswahl verloren, und App-Bloecke werden nicht neu initialisiert.
 *
 * Dependencies:
 * - Custom select component (liefert valueId/variantId im change-Event)
 *
 * Events:
 * - on:variant:change  { form, variant, selectedOptions }   (Detail wie v1 + selectedOptions)
 */

window.theme = window.theme || {};

/** Kleiner Fetch-Cache mit Dedup + Preload (uebernommen aus Enterprise 2.3.0). */
theme.fetchCache = theme.fetchCache || {
  MAX_CACHE_SIZE: 20,
  cache: new Map(),
  pending: new Map(),

  async fetchAndCache(url) {
    const response = await fetch(url);
    if (!response.ok) {
      this.pending.delete(url);
      throw new Error(`fetchCache: HTTP ${response.status}`);
    }
    const text = await response.text();

    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = [...this.cache.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
      this.cache.delete(oldestKey);
    }

    this.cache.set(url, { text, timestamp: Date.now() });
    this.pending.delete(url);
    return text;
  },

  fetch(url) {
    if (this.cache.has(url)) return Promise.resolve(this.cache.get(url).text);
    if (this.pending.has(url)) return this.pending.get(url);

    const promise = this.fetchAndCache(url);
    this.pending.set(url, promise);
    return promise;
  },

  preload(url) {
    this.fetch(url).catch(() => {
      this.cache.delete(url);
      this.pending.delete(url);
    });
  },

  clear() {
    this.cache.clear();
  }
};

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product') || this.closest('.shopify-section');
      this.quickBuyContainer = this.closest('quick-add-drawer');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.data.variant;
      this.requestId = 0;

      this.addListeners();

      // Deep-Link auf eine Variante jenseits der 250 (?variant= / ?option_values=):
      // auch hier kann eine App den Preis nachtraeglich falsch ueberschreiben.
      this.guardServerPrice();
    }

    addListeners() {
      this.boundHandleVariantChange = this.boundHandleVariantChange
        || this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.boundHandleLabelMouseEnter
        || this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Liest den (kleinen) JSON-Block des Pickers: nur die aktuelle Variante.
     * @returns {object}
     */
    getProductData() {
      try {
        const dataEl = this.querySelector(':scope > [type="application/json"]');
        return dataEl ? JSON.parse(dataEl.textContent) : { variant: null, formatted: {} };
      } catch (e) {
        return { variant: null, formatted: {} };
      }
    }

    /** @returns {?Element} Das Produktformular der Section. */
    getProductForm() {
      return this.section ? this.section.querySelector('.js-product-form-main, .js-product-form') : null;
    }

    /**
     * Gewaehlte Optionswerte (Strings) — null, wenn eine Option nicht gewaehlt ist.
     * @returns {Array}
     */
    getSelectedOptions() {
      const out = [];
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
          out.push(sel && sel.dataset.value !== '' ? sel.dataset.value : null);
        } else {
          const sel = selector.querySelector('input:checked');
          out.push(sel ? sel.value : null);
        }
      });
      return out;
    }

    /**
     * IDs der gewaehlten Optionswerte — Grundlage fuer ?option_values=.
     * @returns {Array}
     */
    getSelectedOptionIds() {
      const out = [];
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
          out.push(sel && sel.dataset.value !== '' ? sel.dataset.valueId : null);
        } else {
          const sel = selector.querySelector('input:checked');
          out.push(sel ? sel.dataset.valueId : null);
        }
      });
      return out;
    }

    /**
     * Baut die URL, aus der der neue Variantenzustand geholt wird.
     * @param {Array} optionIds - Gewaehlte Optionswert-IDs.
     * @param {string} combinedProductUrl - URL eines Combined-Listing-Geschwisterprodukts (optional).
     * @returns {string}
     */
    buildUrl(optionIds, combinedProductUrl) {
      const url = new URL(combinedProductUrl || this.dataset.url, window.location.origin);
      const ids = optionIds.filter((id) => id != null);
      if (ids.length) url.searchParams.set('option_values', ids.join(','));

      // Im Quick-Add-Drawer ist die Section-ID durch 'quickadd' ersetzt worden
      // (siehe quick-add.js) -> dort die volle Produktseite holen.
      if (!this.quickBuyContainer && !combinedProductUrl && this.dataset.sectionId) {
        url.searchParams.set('section_id', this.dataset.sectionId);
      }
      return url.toString();
    }

    /**
     * Preload bei Hover/Touch — 250 ms Verzoegerung, um Klick-Absicht abzuwarten.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded || label.dataset.preloadTimeout) return;
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return;

      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      if (!input) return;

      const optionIds = this.getSelectedOptionIds();
      const idx = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      if (idx === -1) return;
      optionIds[idx] = input.dataset.valueId;
      if (optionIds.indexOf(null) !== -1) return;

      label.dataset.preloadTimeout = setTimeout(() => {
        label.dataset.preloaded = 'true';
        label.removeAttribute('data-preload-timeout');
        theme.fetchCache.preload(this.buildUrl(optionIds, input.dataset.productUrl));
      }, 250);
    }

    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Reagiert auf die Auswahl eines Optionswerts.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      VariantPicker.updateLabelText(evt);

      const optionIds = this.getSelectedOptionIds();
      const target = evt.target || {};
      const combinedProductUrl = (target.dataset && target.dataset.productUrl)
        || (evt.detail && evt.detail.productUrl);

      // Combined Listing: anderes Produkt -> ganz normal navigieren.
      if (combinedProductUrl && !this.quickBuyContainer) {
        window.location.href = this.buildUrl(optionIds, combinedProductUrl);
        return;
      }

      const url = this.buildUrl(optionIds, combinedProductUrl);
      this.requestId += 1;
      const requestId = this.requestId;
      this.setAttribute('aria-busy', 'true');

      theme.fetchCache.fetch(url)
        .then((responseText) => {
          if (requestId !== this.requestId) return;
          this.applyResponse(responseText, evt);
        })
        .catch(() => {
          // Netzfehler: bestehenden Zustand stehen lassen, nichts kaputt machen.
        })
        .finally(() => {
          if (requestId === this.requestId) this.removeAttribute('aria-busy');
        });
    }

    /**
     * Uebernimmt den serverseitig gerenderten Zustand.
     * @param {string} responseText - HTML der Section (im Quick-Add: der ganzen Produktseite).
     * @param {object} evt - ausloesendes Event.
     */
    applyResponse(responseText, evt) {
      const doc = new DOMParser().parseFromString(responseText, 'text/html');

      // Im Quick-Add kommt die volle Produktseite -> Section-IDs wie in quick-add.js ersetzen.
      let source = doc.body;
      if (this.quickBuyContainer) {
        const productEl = doc.querySelector('.js-product');
        if (!productEl) return;
        const sectionId = productEl.dataset.section;
        if (sectionId) productEl.innerHTML = productEl.innerHTML.replaceAll(sectionId, 'quickadd');
        source = productEl;
      }

      const newPicker = source.querySelector('variant-picker');
      if (!newPicker) return;

      this.updateOptionStates(newPicker);
      this.replaceDynamicContent(source);
      this.updateVariantData(newPicker);
      this.guardServerPrice();
      this.updateUrl();
      this.updatePickupAvailability();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));

      if (evt && evt.target && evt.target.id && !(evt.detail && evt.detail.noFocus)) {
        const focusEl = document.querySelector(`#${CSS.escape(evt.target.id)}-button`)
          || document.getElementById(evt.target.id);
        if (focusEl && typeof focusEl.focus === 'function') focusEl.focus({ preventScroll: true });
      }
    }

    /**
     * Uebertraegt Verfuegbarkeit/Bestandsampel der Optionswerte aus der frischen Section,
     * ohne die Elemente auszutauschen (Auswahl und Fokus bleiben erhalten).
     * @param {Element} newPicker - Der frisch gerenderte Picker.
     */
    updateOptionStates(newPicker) {
      this.querySelectorAll('.js-option[data-value-id]').forEach((option) => {
        const fresh = newPicker.querySelector(
          `.js-option[data-value-id="${CSS.escape(option.dataset.valueId)}"]`
        );
        if (!fresh) return;

        // aria-selected/checked gehoert dem Nutzer, nicht der Antwort.
        const wasSelected = option.getAttribute('aria-selected');
        option.className = fresh.className;
        if (wasSelected !== null) option.setAttribute('aria-selected', wasSelected);

        if (fresh.dataset.variantId) {
          option.dataset.variantId = fresh.dataset.variantId;
        } else {
          delete option.dataset.variantId;
        }

        // Hinweistext: Button-Stil am Label (title), Dropdown-Stil als <em> im Eintrag.
        if (option.classList.contains('custom-select__option')) {
          const freshEm = fresh.querySelector('em');
          const em = option.querySelector('em');
          if (freshEm && !em) {
            option.appendChild(freshEm.cloneNode(true));
          } else if (freshEm && em) {
            em.textContent = freshEm.textContent;
            em.hidden = false;
          } else if (em) {
            em.remove();
          }
        } else {
          const label = option.nextElementSibling;
          const freshLabel = fresh.nextElementSibling;
          if (label && freshLabel) {
            const title = freshLabel.getAttribute('title');
            if (title) {
              label.setAttribute('title', title);
            } else {
              label.removeAttribute('title');
            }
          }
        }

        // Preload-Marker zuruecksetzen: der Zustand kann sich geaendert haben.
        const nextLabel = option.nextElementSibling;
        if (nextLabel) nextLabel.removeAttribute('data-preloaded');
      });
    }

    /**
     * Tauscht alle mit data-dynamic-variant-content markierten Bereiche gegen die
     * frisch gerenderte Fassung.
     * @param {Element} source - Wurzel der Antwort.
     */
    replaceDynamicContent(source) {
      if (!this.section) return;

      this.section.querySelectorAll('[data-dynamic-variant-content]').forEach((target) => {
        const key = target.dataset.dynamicVariantContent;
        const fresh = source.querySelector(`[data-dynamic-variant-content="${CSS.escape(key)}"]`);
        if (!fresh) return;

        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: { toReplace: target, replaceWith: fresh }
        }));

        if (target.matches('input')) {
          // Nur den Wert uebernehmen. Attribute wie `disabled` gehoeren dem Formular
          // (product-form.js schaltet das id-Feld beim Init frei) und duerfen nicht
          // aus der Antwort zurueckgesetzt werden.
          target.value = fresh.value || fresh.getAttribute('value') || '';
          setTimeout(() => target.dispatchEvent(new Event('change', { bubbles: true })), 0);
          return;
        }

        // Eingaben des Nutzers (z. B. Menge) in die neue Fassung uebernehmen.
        target.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          const freshInput = fresh.querySelector(`#${CSS.escape(sourceInput.id)}`);
          if (!freshInput) return;
          if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
            freshInput.toggleAttribute('checked', sourceInput.checked);
          } else {
            freshInput.value = sourceInput.value;
          }
        });

        target.replaceWith(fresh);
      });
    }

    /**
     * Uebernimmt die Variantendaten aus der Antwort (inkl. featured_media fuer die Galerie).
     * @param {Element} newPicker - Der frisch gerenderte Picker.
     */
    updateVariantData(newPicker) {
      let fresh = { variant: null, formatted: {} };
      try {
        const dataEl = newPicker.querySelector(':scope > [type="application/json"]');
        if (dataEl) fresh = JSON.parse(dataEl.textContent);
      } catch (e) {
        fresh = { variant: null, formatted: {} };
      }

      this.data = fresh;
      this.variant = fresh.variant;

      // Eigenen JSON-Block mitziehen, damit spaetere Leser (Quick-Add-Klone,
      // Theme-Editor-Reinit) den aktuellen Stand sehen.
      const ownDataEl = this.querySelector(':scope > [type="application/json"]');
      if (ownDataEl) ownDataEl.textContent = JSON.stringify(fresh);
    }

    /**
     * Haelt den serverseitig gerenderten Preis gegen Fremdschreiber.
     *
     * Warum: Das 250er-Limit von `product.variants` trifft nicht nur das Theme, sondern auch
     * App-Bloecke. Die Rabatt-App (Regios) bettet ihre eigene Variantentabelle ein — ebenfalls
     * bei 250 abgeschnitten — und schreibt den Preis nach ihrer Verzoegerung neu. Findet sie die
     * gewaehlte Variante nicht, setzt sie den Preis der ERSTEN Variante. Auf einem Produkt mit
     * 1412 Varianten stand dann 36,99 € statt 193,99 € im Preisblock.
     *
     * Deshalb: nur bei High-Variant-Produkten, nur bei Menge 1 (ab Menge 2 gehoert der
     * Preisblock der Mengenstaffel), nur fuenf Korrekturen und nur fuer fuenf Sekunden.
     * Sobald die App ihre Variantentabelle nicht mehr kappt, kann das hier ersatzlos weg.
     */
    guardServerPrice() {
      if (this.dataset.highVariant !== 'true' || !this.section) return;
      if (this.priceGuard) this.priceGuard.disconnect();

      const container = this.section.querySelector('[data-dynamic-variant-content="price"]');
      const priceEl = container && container.querySelector('.price__default .price__current');
      if (!priceEl) return;

      const expectedText = priceEl.textContent.trim();
      const expectedHtml = priceEl.innerHTML;
      if (!expectedText) return;

      const qtyEl = this.section.querySelector('[name="quantity"]');
      let corrections = 0;

      const observer = new MutationObserver(() => {
        if (!priceEl.isConnected || corrections >= 5) {
          observer.disconnect();
          return;
        }
        const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
        if (qty !== 1) return;
        if (priceEl.textContent.trim() === expectedText) return;

        corrections += 1;
        priceEl.innerHTML = expectedHtml;
      });

      observer.observe(priceEl, { childList: true, characterData: true, subtree: true });
      this.priceGuard = observer;
      setTimeout(() => observer.disconnect(), 5000);
    }

    /** Aktualisiert ?variant= in der Adresszeile. */
    updateUrl() {
      if (this.dataset.updateUrl === 'false' || this.quickBuyContainer) return;
      const url = this.variant
        ? `${this.dataset.url}?variant=${this.variant.id}`
        : this.dataset.url;
      window.history.replaceState({}, '', url);
    }

    /** Stoesst die Abholverfuegbarkeit fuer die neue Variante an. */
    updatePickupAvailability() {
      const pickup = this.section ? this.section.querySelector('pickup-availability') : null;
      if (!pickup) return;

      if (this.variant && this.variant.available && typeof pickup.getAvailability === 'function') {
        pickup.getAvailability(this.variant.id);
      } else {
        pickup.removeAttribute('available');
        pickup.innerHTML = '';
      }
    }

    /**
     * Setzt den Farbnamen im Label sofort — noch bevor die Antwort da ist.
     * @param {object} evt - Event object.
     */
    static updateLabelText(evt) {
      const selector = evt.target && evt.target.closest
        ? evt.target.closest('.option-selector')
        : null;
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const label = evt.target.nextElementSibling;
      const value = label && label.querySelector('.js-value');
      colorText.textContent = value ? value.textContent : evt.target.value;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
